import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { useNotifications } from '../contexts/NotificationContext';
import { COMMON_STYLES } from '../utils/constants';

// Styles locaux
const STYLES = {
  overlay: { ...COMMON_STYLES.MODAL_OVERLAY, background: 'rgba(0,0,0,0.7)', zIndex: 2000 },
  modal: { ...COMMON_STYLES.MODAL_CONTENT, width: '800px', maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  header: { padding: '20px 24px', background: '#ff6b6b', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerTitle: { margin: 0, fontSize: '20px' },
  headerSubtitle: { margin: '4px 0 0 0', opacity: 0.9, fontSize: '14px' },
  closeBtn: { ...COMMON_STYLES.CLOSE_BUTTON, background: 'rgba(255,255,255,0.2)', borderRadius: '6px', width: '32px', height: '32px', color: 'white' },
  content: { padding: '24px', maxHeight: 'calc(90vh - 140px)', overflow: 'auto' },
  footer: { padding: '16px 24px', borderTop: '1px solid #eee', background: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  footerInfo: { fontSize: '13px', color: '#666' },
  footerButtons: { display: 'flex', gap: '12px' },
  conflictCard: { border: '1px solid #ff6b6b', borderRadius: '8px', padding: '16px', marginBottom: '16px', background: '#fff5f5' },
  conflictHeader: { display: 'flex', alignItems: 'center', marginBottom: '12px' },
  conflictIcon: { fontSize: '20px', marginRight: '8px' },
  conflictTitle: { margin: 0, color: '#d63031' },
  conflictDesc: { marginBottom: '12px', fontSize: '14px', color: '#666' },
  conflictDetails: { background: '#fff', padding: '12px', borderRadius: '4px', marginBottom: '16px', fontSize: '13px' },
  radioLabel: (selected) => ({ display: 'flex', alignItems: 'center', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', background: selected ? '#e3f2fd' : 'white' }),
  suggestionItem: { display: 'flex', alignItems: 'center', padding: '6px', border: '1px solid #eee', marginBottom: '4px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  actionBtn: (bg, disabled) => ({ padding: '10px 20px', background: disabled ? '#6c757d' : bg, color: 'white', border: 'none', borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '14px' }),
  loadSuggestionsBtn: { padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  suggestionsContainer: { marginTop: '8px' },
  suggestionsTitle: { margin: '8px 0 4px 0' },
  suggestionsScroll: { maxHeight: '150px', overflow: 'auto' },
  suggestionName: { fontWeight: '600' },
  suggestionDetails: { color: '#666' },
  dateInput: { padding: '6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' },
  dateInputContainer: { marginTop: '12px' },
  dateInputTitle: { margin: '8px 0 4px 0' },
  dateInputGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  actionResultBox: (type) => ({ marginTop: '12px' }),
  actionResultInner: (type) => ({
    background: type === 'success' ? '#d4edda' : '#f8d7da',
    border: `1px solid ${type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
    color: type === 'success' ? '#155724' : '#721c24',
    padding: '8px', borderRadius: '4px', fontSize: '12px'
  }),
  actionResultText: { marginTop: '4px' },
  actionResultItalic: { marginTop: '4px', fontStyle: 'italic' },
  actionsTitle: { margin: '0 0 8px 0' },
  actionsGrid: { display: 'grid', gap: '8px' },
  participantList: { margin: '4px 0', paddingLeft: '20px' },
  confirmOverlay: { ...COMMON_STYLES.MODAL_OVERLAY, background: 'rgba(0,0,0,0.7)', zIndex: 3000, padding: '20px' },
  confirmModal: { background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '450px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  confirmTitle: { margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#333' },
  confirmMessage: { margin: '0 0 24px 0', fontSize: '14px', color: '#666', whiteSpace: 'pre-wrap' },
  confirmButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  confirmCancelBtn: { padding: '10px 20px', background: '#f5f5f5', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  confirmDeleteBtn: { padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }
};

const ConflictResolutionModal = ({ conflicts, onResolve, onCancel, token }) => {
  const { showSuccess, showError, showWarning } = useNotifications();
  const [resolution, setResolution] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  logger.debug('🚀 === CONFLICT RESOLUTION MODAL - MONTAGE ===');
  logger.debug('📋 Conflits reçus:', conflicts);
  logger.debug('🔧 Callbacks:', { onResolve: !!onResolve, onCancel: !!onCancel, token: !!token });

  // Types de conflits
  const conflictTypes = {
    room: 'Conflit de salle',
    trainer: 'Conflit de formateur', 
    participant: 'Conflit de participant',
    holiday: 'Jour férié'
  };

  // Actions possibles pour résoudre un conflit
  const resolutionActions = {
    force: 'Forcer (ignorer le conflit)',
    replace: 'Remplacer la ressource',
    reschedule: 'Reprogrammer',
    cancel: 'Annuler la création',
    keep_and_replace: 'Garder ici et remplacer ailleurs', // ✅ NOUVEAU
    remove_from_other: 'Supprimer de l\'autre session' // ✅ NOUVEAU
  };

  // ✅ NOUVEAU : Log des changements de résolution
  useEffect(() => {
    logger.debug('🔄 Résolutions mises à jour:', resolution);
    logger.debug('📊 Résolutions count:', Object.keys(resolution).length, '/', conflicts.length);
  }, [resolution, conflicts.length]);

  // Charger les suggestions pour résoudre les conflits
  const loadSuggestions = async (conflict) => {
    try {
      setLoading(true);
      logger.debug('🔍 === CHARGEMENT SUGGESTIONS ===');
      logger.debug('⚡ Conflit:', conflict);
      
      if (conflict.type === 'room') {
        logger.debug('🏢 Chargement suggestions salles...');
        const response = await fetch(API_ENDPOINTS.GROUP_ALTERNATIVES, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            city: conflict.room_city || conflict.group_city,
            capacity: conflict.required_capacity,
            start_date: conflict.start_date,
            end_date: conflict.end_date,
            exclude_room_id: conflict.room_id || conflict.group_id
          })
        });
        const data = await response.json();
        logger.debug('🏢 Réponse suggestions salles:', data);
        if (data.success) {
          setSuggestions(prev => ({
            ...prev,
            [conflict.id]: { type: 'rooms', data: data.data }
          }));
        }
      } else if (conflict.type === 'trainer') {
        logger.debug('👨‍🏫 Chargement suggestions formateurs...');
        const response = await fetch(API_ENDPOINTS.USER_TEAM_LEADER_ALTERNATIVES, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            specialties: conflict.required_specialties,
            start_date: conflict.start_date,
            end_date: conflict.end_date,
            exclude_team_leader_id: conflict.trainer_id || conflict.team_leader_id
          })
        });
        const data = await response.json();
        logger.debug('👨‍🏫 Réponse suggestions formateurs:', data);
        if (data.success) {
          setSuggestions(prev => ({
            ...prev,
            [conflict.id]: { type: 'trainers', data: data.data }
          }));
        }
      } else if (conflict.type === 'participant') {
        logger.debug('👥 Chargement suggestions participants...');
        
        // ✅ NOUVEAU : Charger les utilisateurs disponibles pour remplacement
        const availableUsersResponse = await fetch(API_ENDPOINTS.USERS_FIND_REPLACEMENTS, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            conflicted_user_ids: conflict.conflicted_participants.map(p => p.id),
            start_date: conflict.start_date,
            end_date: conflict.end_date,
            same_team_only: false
          })
        });
        
        const availableUsersData = await availableUsersResponse.json();
        logger.debug('👥 Réponse utilisateurs disponibles:', availableUsersData);
        
        // Suggestions de créneaux alternatifs
        const response = await fetch(API_ENDPOINTS.PRESTATION_ALTERNATIVES, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            participant_ids: conflict.conflicted_participants.map(p => p.id),
            start_date: conflict.start_date,
            end_date: conflict.end_date,
            room_id: conflict.room_id || conflict.group_id,
            trainer_id: conflict.trainer_id || conflict.team_leader_id
          })
        });
        const data = await response.json();
        logger.debug('👥 Réponse suggestions créneaux:', data);
        
        if (data.success || availableUsersData.success) {
          setSuggestions(prev => ({
            ...prev,
            [conflict.id]: { 
              type: 'alternatives', 
              data: data.data || [],
              available_users: availableUsersData.data || []
            }
          }));
        }
      }
    } catch (error) {
      logger.error('❌ Erreur chargement suggestions:', error);
    } finally {
      setLoading(false);
      logger.debug('✅ === FIN CHARGEMENT SUGGESTIONS ===');
    }
  };

  // Gérer la sélection d'une action de résolution
  const handleResolutionChange = (conflictId, action, data = null) => {
    logger.debug('🔧 === CHANGEMENT RÉSOLUTION ===');
    logger.debug('🆔 Conflict ID:', conflictId);
    logger.debug('⚡ Action:', action);
    logger.debug('📦 Data:', data);
    
    setResolution(prev => {
      const newResolution = {
        ...prev,
        [conflictId]: { action, data }
      };
      logger.debug('📊 Nouvelle résolution complète:', newResolution);
      return newResolution;
    });
    
    logger.debug('✅ === FIN CHANGEMENT RÉSOLUTION ===');
  };

  // ✅ NOUVEAU : Fonction pour gérer les résolutions complexes
  const handleComplexResolution = async (conflictId, action, conflict) => {
    logger.debug('🔀 === RÉSOLUTION COMPLEXE ===');
    logger.debug('🆔 Conflict ID:', conflictId);
    logger.debug('⚡ Action:', action);
    logger.debug('📋 Conflit:', conflict);
    
    if (action === 'keep_and_replace') {
      logger.debug('🔄 KEEP_AND_REPLACE détecté');
      
      // Charger les suggestions si pas déjà fait
      if (!suggestions[conflictId]) {
        logger.debug('📥 Chargement suggestions pour KEEP_AND_REPLACE...');
        await loadSuggestions(conflict);
      }
      
      const conflictSuggestions = suggestions[conflictId];
      logger.debug('💡 Suggestions disponibles:', conflictSuggestions);
      
      if (conflictSuggestions?.available_users?.length > 0) {
        logger.debug('✅ Utilisateurs disponibles trouvés:', conflictSuggestions.available_users.length);
        
        // Afficher une interface de sélection de remplacement
        const availableUsers = conflictSuggestions.available_users;
        logger.debug('👥 Liste utilisateurs disponibles:', availableUsers);
        
        // ✅ Pour le moment, sélectionner automatiquement le premier disponible
        // TODO: Implémenter une vraie interface de sélection
        const selectedUser = availableUsers[0];
        logger.debug('🎯 Utilisateur sélectionné automatiquement:', selectedUser);
        
        handleResolutionChange(conflictId, 'keep_and_replace', {
          replacement_user: selectedUser,
          conflicted_sessions: conflict.conflicted_participants[0]?.conflicting_sessions || []
        });
      } else {
        logger.debug('⚠️ Aucun utilisateur disponible - basculer vers remove_from_other');

        // Aucun utilisateur disponible, proposer la suppression via modal
        const participantName = conflict.conflicted_participants[0]?.name || 'cet utilisateur';
        setConfirmModal({
          show: true,
          title: 'Aucun remplaçant disponible',
          message: `Aucun remplaçant disponible trouvé.\n\nVoulez-vous supprimer ${participantName} de l'autre session en conflit ?\n\n⚠️ Cette action supprimera définitivement le participant de l'autre formation.`,
          onConfirm: () => {
            setConfirmModal({ ...confirmModal, show: false });
            logger.debug('🤔 Confirmation suppression: true');
            handleResolutionChange(conflictId, 'remove_from_other', {
              conflicted_sessions: conflict.conflicted_participants[0]?.conflicting_sessions || []
            });
          }
        });
        return;
      }
    } else {
      logger.debug('⚡ Action simple:', action);
      handleResolutionChange(conflictId, action);
    }
    
    logger.debug('✅ === FIN RÉSOLUTION COMPLEXE ===');
  };

  // Appliquer les résolutions
  const applyResolutions = async () => {
    try {
      setLoading(true);
      logger.debug('🚀 === APPLICATION DES RÉSOLUTIONS ===');
      logger.debug('📋 Résolutions à appliquer:', resolution);
      logger.debug('🔢 Nombre de résolutions:', Object.keys(resolution).length);
      logger.debug('🔢 Nombre de conflits:', conflicts.length);
      
      if (Object.keys(resolution).length !== conflicts.length) {
        console.warn('⚠️ Pas toutes les résolutions définies');
        showWarning('Veuillez définir une action pour tous les conflits');
        setLoading(false);
        return;
      }
      
      const resolutionsData = Object.entries(resolution).map(([conflictId, res]) => {
        logger.debug(`📦 Préparation résolution ${conflictId}:`, res);
        return {
          conflict_id: conflictId,
          action: res.action,
          data: res.data
        };
      });
      
      logger.debug('📤 Données envoyées au backend:', resolutionsData);

      const response = await fetch(API_ENDPOINTS.PRESTATION_RESOLVE_CONFLICTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolutions: resolutionsData })
      });

      logger.debug('📡 Réponse backend status:', response.status);
      
      const result = await response.json();
      logger.debug('📋 Réponse backend complète:', result);
      
      if (result.success) {
        logger.debug('✅ Résolutions appliquées avec succès');
        logger.debug('📊 Résultat final:', result.data);
        showSuccess('Conflits résolus avec succès');

        if (onResolve) {
          logger.debug('🔄 Appel callback onResolve...');
          onResolve(result.data);
        } else {
          console.warn('⚠️ Pas de callback onResolve défini');
        }
      } else {
        logger.error('❌ Erreur backend:', result.error);
        showError('Erreur lors de la résolution des conflits: ' + result.error);
      }
    } catch (error) {
      logger.error('❌ Erreur résolution conflits:', error);
      logger.error('❌ Stack trace:', error.stack);
      showError('Erreur de connexion: ' + error.message);
    } finally {
      setLoading(false);
      logger.debug('🏁 === FIN APPLICATION DES RÉSOLUTIONS ===');
    }
  };

  // Interface pour un conflit individuel
  const ConflictItem = ({ conflict }) => {
    logger.debug('🎨 Rendu ConflictItem:', conflict);

    return (
      <div style={STYLES.conflictCard}>
        <div style={STYLES.conflictHeader}>
          <span style={STYLES.conflictIcon}>⚠️</span>
          <h4 style={STYLES.conflictTitle}>{conflictTypes[conflict.type]}</h4>
        </div>

        <div style={STYLES.conflictDesc}>{conflict.description}</div>

        <div style={STYLES.conflictDetails}>
          {conflict.type === 'participant' && (
            <div>
              <strong>Participants en conflit:</strong>
              <ul style={STYLES.participantList}>
                {conflict.conflicted_participants?.map(p => <li key={p.id}>{p.name} - {p.conflicting_session}</li>)}
              </ul>
            </div>
          )}
          {conflict.existing_session && <div><strong>Session existante:</strong> {conflict.existing_session}</div>}
          <div><strong>Période:</strong> {conflict.start_date} au {conflict.end_date}</div>
        </div>

        {/* Actions de résolution */}
        <div>
          <h5 style={STYLES.actionsTitle}>Action de résolution:</h5>
          <div style={STYLES.actionsGrid}>
            {Object.entries(resolutionActions).map(([action, label]) => {
              if (conflict.type !== 'participant' && ['keep_and_replace', 'remove_from_other'].includes(action)) return null;
              return (
                <label key={action} style={STYLES.radioLabel(resolution[conflict.id]?.action === action)}>
                  <input type="radio" name={`resolution-${conflict.id}`} value={action} checked={resolution[conflict.id]?.action === action}
                    onChange={() => {
                      logger.debug(`🔘 Radio change: ${conflict.id} -> ${action}`);
                      if (['keep_and_replace', 'remove_from_other'].includes(action)) handleComplexResolution(conflict.id, action, conflict);
                      else handleResolutionChange(conflict.id, action);
                    }} style={{ marginRight: '8px' }} />
                  {label}
                </label>
              );
            })}
          </div>

          {resolution[conflict.id]?.action === 'replace' && (
            <div style={STYLES.actionResultBox('replace')}>
              <button onClick={() => { logger.debug('🔍 Chargement suggestions...'); loadSuggestions(conflict); }} style={STYLES.loadSuggestionsBtn}>
                🔍 Charger les suggestions
              </button>
              {suggestions[conflict.id] && (
                <div style={STYLES.suggestionsContainer}>
                  <h6 style={STYLES.suggestionsTitle}>Suggestions de remplacement:</h6>
                  <div style={STYLES.suggestionsScroll}>
                    {suggestions[conflict.id].data.map(item => (
                      <label key={item.id} style={STYLES.suggestionItem}>
                        <input type="radio" name={`replacement-${conflict.id}`} value={item.id}
                          onChange={() => { logger.debug(`🔄 Sélection: ${item.id}`, item); handleResolutionChange(conflict.id, 'replace', item); }}
                          style={{ marginRight: '8px' }} />
                        <div>
                          <div style={STYLES.suggestionName}>{item.name}</div>
                          <div style={STYLES.suggestionDetails}>{item.details || item.city || item.specialties?.join(', ')}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Affichage des remplaçants pour keep_and_replace */}
          {resolution[conflict.id]?.action === 'keep_and_replace' && (
            <div style={STYLES.actionResultBox('success')}>
              <div style={STYLES.actionResultInner('success')}>
                ✅ <strong>Action :</strong> Garder ici et remplacer dans l'autre session
                {resolution[conflict.id]?.data?.replacement_user && (
                  <div style={STYLES.actionResultText}>
                    <strong>Remplaçant :</strong> {resolution[conflict.id].data.replacement_user.full_name}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Affichage de la suppression pour remove_from_other */}
          {resolution[conflict.id]?.action === 'remove_from_other' && (
            <div style={STYLES.actionResultBox('danger')}>
              <div style={STYLES.actionResultInner('danger')}>
                🗑️ <strong>Action :</strong> Supprimer de l'autre session
                <div style={STYLES.actionResultItalic}>
                  Le participant sera retiré de la formation en conflit
                </div>
              </div>
            </div>
          )}

          {/* Options de reprogrammation */}
          {resolution[conflict.id]?.action === 'reschedule' && (
            <div style={STYLES.dateInputContainer}>
              <h6 style={STYLES.dateInputTitle}>Nouvelle période:</h6>
              <div style={STYLES.dateInputGrid}>
                <input type="date" placeholder="Date de début" style={STYLES.dateInput}
                  onChange={(e) => {
                    logger.debug(`📅 Nouvelle date début: ${e.target.value}`);
                    handleResolutionChange(conflict.id, 'reschedule', { ...resolution[conflict.id]?.data, start_date: e.target.value });
                  }} />
                <input type="date" placeholder="Date de fin" style={STYLES.dateInput}
                  onChange={(e) => {
                    logger.debug(`📅 Nouvelle date fin: ${e.target.value}`);
                    handleResolutionChange(conflict.id, 'reschedule', { ...resolution[conflict.id]?.data, end_date: e.target.value });
                  }} />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const allResolved = Object.keys(resolution).length === conflicts.length;

  return (
    <div style={STYLES.overlay}>
      <div style={STYLES.modal}>
        {/* En-tête */}
        <div style={STYLES.header}>
          <div>
            <h2 style={STYLES.headerTitle}>⚠️ Résolution des Conflits</h2>
            <p style={STYLES.headerSubtitle}>
              {conflicts.length} conflit{conflicts.length > 1 ? 's' : ''} détecté{conflicts.length > 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={() => { logger.debug('❌ Annulation résolution conflits'); onCancel(); }} style={STYLES.closeBtn}>✕</button>
        </div>

        {/* Contenu */}
        <div style={STYLES.content}>
          {conflicts.map(conflict => (
            <ConflictItem key={conflict.id} conflict={conflict} />
          ))}
        </div>

        {/* Footer */}
        <div style={STYLES.footer}>
          <div style={STYLES.footerInfo}>
            Résolutions définies: {Object.keys(resolution).length}/{conflicts.length}
          </div>
          <div style={STYLES.footerButtons}>
            <button onClick={() => { logger.debug('❌ Annulation résolution conflits (footer)'); onCancel(); }} style={STYLES.actionBtn('#6c757d', false)}>
              Annuler
            </button>
            <button onClick={() => { logger.debug('✅ Demande application résolutions'); applyResolutions(); }}
              disabled={loading || !allResolved} style={STYLES.actionBtn(allResolved ? '#28a745' : '#6c757d', !allResolved)}>
              {loading ? '⏳ Application...' : '✅ Appliquer les résolutions'}
            </button>
          </div>
        </div>
      </div>

      {/* Modal de confirmation */}
      {confirmModal.show && (
        <div style={STYLES.confirmOverlay}>
          <div style={STYLES.confirmModal}>
            <h3 style={STYLES.confirmTitle}>{confirmModal.title}</h3>
            <p style={STYLES.confirmMessage}>{confirmModal.message}</p>
            <div style={STYLES.confirmButtons}>
              <button onClick={() => setConfirmModal({ ...confirmModal, show: false })} style={STYLES.confirmCancelBtn}>Annuler</button>
              <button onClick={confirmModal.onConfirm} style={STYLES.confirmDeleteBtn}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConflictResolutionModal;